import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import { useAuth } from '../context/AuthContext';
import { API_BASE_URL, INITIAL_SYNC_BLOCKS } from '../config/env';
import { prefetchApi } from '../hooks/useApi';
import '../styles/login-premium.css';

const SYNC_STORAGE_KEY = 'pulsoone_initial_sync_done';
// Allow any username — backend will validate credentials

async function parseJsonResponse(res) {
  const text = await res.text();

  try {
    return text ? JSON.parse(text) : {};
  } catch (_) {
    if (!res.ok && text.trim().startsWith('<')) {
      throw new Error(
        'El servidor devolvió HTML en lugar de JSON. Verificá la configuración del dominio o del proxy.'
      );
    }

    throw new Error('La respuesta del servidor no llegó en formato JSON válido.');
  }
}

function getSyncAlreadyDone() {
  try {
    return localStorage.getItem(SYNC_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

function setSyncDone() {
  try {
    localStorage.setItem(SYNC_STORAGE_KEY, '1');
  } catch (_) {}
}

async function fetchPermissionCodes() {
  try {
    const res = await fetch(`${API_BASE_URL}/api/auth/permissions`, {
      credentials: 'include'
    });
    if (!res.ok) return [];
    const rows = await res.json();
    return Array.isArray(rows) ? rows.map((row) => String(row.code)) : [];
  } catch {
    return [];
  }
}

function resolvePostLoginPath({ maintenanceBypass = false } = {}) {
  return maintenanceBypass ? '/admin/configuracion' : '/';
}

/* Colores CRM: navy + naranja */
const CRM = {
  orange: '#2365AB',
  orangeDark: '#1C4F86',
  orangeLight: '#FFF1EE',
  navy: '#253342',
  navyMid: '#33475B',
  slate: '#516F90',
  blueLight: '#F5F8FA',
  border: '#DFE3EB',
  borderDark: '#CBD6E2',
  text: '#33475B',
  textLight: '#516F90',
  textXlight: '#7C98B6',
  white: '#FFFFFF'
};

const LOGIN_HIGHLIGHTS = [
  'Acceso seguro con sesiones protegidas',
  'Sincronización inteligente al ingresar',
  'Interfaz optimizada para guardias y escritorio'
];

const LOGIN_TRUST_ITEMS = [
  { icon: 'shield', label: 'Autenticación reforzada' },
  { icon: 'refresh', label: 'Carga inicial optimizada' },
  { icon: 'check', label: 'Flujo guiado y seguro' }
];

function UiIcon({ name, size = 16, stroke = 1.9 }) {
  const common = {
    width: size,
    height: size,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: stroke,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
    'aria-hidden': true
  };
  switch (name) {
    case 'user':
      return <svg {...common}><path d="M20 21a8 8 0 0 0-16 0" /><circle cx="12" cy="8" r="4" /></svg>;
    case 'lock':
      return <svg {...common}><rect x="4" y="11" width="16" height="10" rx="2" /><path d="M8 11V8a4 4 0 0 1 8 0v3" /></svg>;
    case 'mail':
      return <svg {...common}><rect x="3" y="5" width="18" height="14" rx="2" /><path d="m4 7 8 6 8-6" /></svg>;
    case 'check':
      return <svg {...common}><circle cx="12" cy="12" r="9" /><path d="m8.6 12.4 2.2 2.2 4.6-4.6" /></svg>;
    case 'shield':
      return <svg {...common}><path d="M12 3 5 6v6c0 5 3.5 7.8 7 9 3.5-1.2 7-4 7-9V6l-7-3Z" /><path d="m9.3 12.1 1.8 1.8 3.6-3.6" /></svg>;
    case 'eye':
      return <svg {...common}><path d="M2 12s3.5-6 10-6 10 6 10 6-3.5 6-10 6-10-6-10-6Z" /><circle cx="12" cy="12" r="2.8" /></svg>;
    case 'eye-off':
      return <svg {...common}><path d="M4 4 20 20" /><path d="M10.7 6.2A10.8 10.8 0 0 1 12 6c6.5 0 10 6 10 6a18 18 0 0 1-3.3 4.2" /><path d="M6.2 8.5A18.5 18.5 0 0 0 2 12s3.5 6 10 6c.7 0 1.4-.1 2-.2" /></svg>;
    case 'spark':
      return <svg {...common}><path d="m12 3 1.6 4.4L18 9l-4.4 1.6L12 15l-1.6-4.4L6 9l4.4-1.6L12 3Z" /></svg>;
    case 'refresh':
      return <svg {...common}><path d="M21 12a9 9 0 1 1-2.6-6.4" /><path d="M21 4v6h-6" /></svg>;
    case 'alert':
      return <svg {...common}><path d="M12 4 3 20h18L12 4Z" /><path d="M12 9v5" /><path d="M12 17h.01" /></svg>;
    default:
      return null;
  }
}

function getPasswordStrengthLabel(passwordValue) {
  const value = String(passwordValue || '');
  if (!value) return { score: 0, label: 'Sin contraseña', tone: '#7C98B6' };

  let score = 0;
  if (value.length >= 8) score += 1;
  if (/[a-z]/.test(value) && /[A-Z]/.test(value)) score += 1;
  if (/\d/.test(value)) score += 1;
  if (/[^A-Za-z0-9]/.test(value)) score += 1;

  if (score <= 1) return { score, label: 'Seguridad baja', tone: '#dc2626' };
  if (score === 2) return { score, label: 'Seguridad media', tone: '#d97706' };
  if (score === 3) return { score, label: 'Seguridad buena', tone: '#2563eb' };
  return { score, label: 'Seguridad alta', tone: '#059669' };
}

function Login() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showFirstLoginModal, setShowFirstLoginModal] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);
  const [changeError, setChangeError] = useState('');
  const [showCurrentPass, setShowCurrentPass] = useState(false);
  const [showNewPass, setShowNewPass] = useState(false);
  const [showConfirmNewPass, setShowConfirmNewPass] = useState(false);
  const [showSyncModal, setShowSyncModal] = useState(false);
  const [syncPercent, setSyncPercent] = useState(0);
  const [syncPhase, setSyncPhase] = useState('Preparando sincronización…');
  const [syncDetail, setSyncDetail] = useState('');
  const [isForgotFlipped, setIsForgotFlipped] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotError, setForgotError] = useState('');
  const [forgotSuccess, setForgotSuccess] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [resetToken, setResetToken] = useState('');
  const [resetPassword, setResetPassword] = useState('');
  const [resetConfirmPassword, setResetConfirmPassword] = useState('');
  const [resetError, setResetError] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [showResetPass, setShowResetPass] = useState(false);
  const [showResetConfirmPass, setShowResetConfirmPass] = useState(false);
  const { login, maintenanceActive } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { maintenanceKey } = useParams();
  const superAdminBypassKey = String(maintenanceKey || '').trim();
  const isSuperAdminBypassMode = superAdminBypassKey.length > 0;

  const canSubmit = username.trim() && password.trim();
  const canRequestForgot = forgotEmail.trim() && !forgotLoading;
  const canSubmitReset =
    resetPassword.trim() &&
    resetConfirmPassword.trim() &&
    !resetLoading;
  const isResetMode = Boolean(resetToken);
  const passwordStrength = getPasswordStrengthLabel(password);
  const currentHour = new Date().getHours();
  const welcomeCopy =
    currentHour < 12
      ? 'Buen día. Todo listo para empezar.'
      : currentHour < 19
        ? 'Buenas tardes. Continuemos con tu gestión.'
        : 'Buenas noches. Seguimos conectados.';
  const isSslActive = typeof window !== 'undefined' && window.location.protocol === 'https:';

  const passwordsFilled =
    currentPassword.trim() &&
    newPassword.trim() &&
    confirmNewPassword.trim();

  const passwordsMatch = newPassword === confirmNewPassword;

  const passwordMinLength = newPassword.length >= 8;
  const passwordHasUpper = /[A-Z]/.test(newPassword);
  const passwordHasLower = /[a-z]/.test(newPassword);
  const passwordHasNumber = /\d/.test(newPassword);
  const passwordHasSpecial = /[^A-Za-z0-9]/.test(newPassword);

  const passwordMeetsAllRules =
    passwordMinLength &&
    passwordHasUpper &&
    passwordHasLower &&
    passwordHasNumber &&
    passwordHasSpecial;

  const isNewPasswordDifferent =
    !newPassword || !currentPassword || newPassword !== currentPassword;

  // Botón del modal: solo depende de que estén los 3 campos llenos
  const canChangePassword = passwordsFilled && !changingPassword;

  // maintenance state is provided by AuthContext (real-time polling)

  const runInitialSync = async () => {
    setShowSyncModal(true);
    setSyncPercent(0);
    setSyncPhase('Preparando sincronización…');
    setSyncDetail('Leyendo metadatos de clientes…');

    try {
      const PAGE_SIZE = 50;

      const url = `${API_BASE_URL}/api/asociacion?page=1&limit=${PAGE_SIZE}&search=`;
      const res = await fetch(url, { credentials: 'include' });
      if (!res.ok) {
        throw new Error('No se pudo preparar clientes');
      }
      const json = await parseJsonResponse(res);
      const total = json.total || 0;
      const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

      const endpoints = [];
      for (let p = 1; p <= totalPages; p += 1) {
        endpoints.push(`/api/asociacion?page=${p}&limit=${PAGE_SIZE}&search=`);
      }

      // Permite limitar la cantidad de bloques a sincronizar
      // REACT_APP_INITIAL_SYNC_BLOCKS:
      //   - número (ej: "100")  -> sincroniza hasta ese máximo de bloques
      //   - "ALL" o vacío       -> sincroniza todos los bloques
      let maxBlocks = endpoints.length;
      if (INITIAL_SYNC_BLOCKS && INITIAL_SYNC_BLOCKS !== 'ALL') {
        const parsed = parseInt(INITIAL_SYNC_BLOCKS, 10);
        if (!Number.isNaN(parsed) && parsed > 0) {
          maxBlocks = Math.min(parsed, endpoints.length);
        }
      }

      const endpointsToPrefetch = endpoints.slice(0, maxBlocks);

      const totalEndpoints = Math.max(1, endpointsToPrefetch.length);
      setSyncPhase('Sincronizando clientes…');
      setSyncDetail(`0 de ${totalEndpoints} bloques listos`);

      await prefetchApi(endpointsToPrefetch, {
        onProgress: ({ completed }) => {
          const clamped = Math.min(completed, totalEndpoints);
          const percent = Math.round((clamped / totalEndpoints) * 100);
          setSyncPercent(percent);
          setSyncDetail(`${clamped} de ${totalEndpoints} bloques listos`);
        }
      });

      setSyncPercent(100);
      setSyncPhase('Sincronización completa');
      setSyncDetail('Listo para trabajar sin esperas.');
      await new Promise((r) => setTimeout(r, 500));
    } catch (e) {
      setSyncPhase('Sincronización parcial');
      setSyncDetail('Algunos datos podrían cargarse al navegar.');
      await new Promise((r) => setTimeout(r, 500));
    } finally {
      setShowSyncModal(false);
      setSyncDone();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit || (maintenanceActive && !isSuperAdminBypassMode)) return;

    // No frontend whitelist; send credentials to backend for validation

    setError('');
    setLoading(true);

    try {
      const res = await fetch(`${API_BASE_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          username,
          password,
          ...(isSuperAdminBypassMode ? { superAdminKey: superAdminBypassKey } : {})
        })
      });

      const data = await parseJsonResponse(res);

      if (res.status === 503) {
        setError(data.message || 'Sistema en mantenimiento. Intente más tarde.');
        return;
      }

      if (!res.ok) {
        throw new Error(data.message || 'Error de autenticación');
      }

      const confirmedMaintenanceBypass = Boolean(data?.maintenanceBypass);

      const userPayload = {
        ...(data.user || { username }),
        ...(confirmedMaintenanceBypass ? { superAdminBypass: true } : {})
      };
      const permissionCodes = await fetchPermissionCodes();
      login({
        ...userPayload,
        ...(permissionCodes.length ? { permissions: permissionCodes } : {})
      });

      if (data.mustChangePassword) {
        setShowFirstLoginModal(true);
      } else {
        setLoading(false);
        if (!getSyncAlreadyDone()) {
          await runInitialSync();
        }
        navigate(resolvePostLoginPath({ maintenanceBypass: confirmedMaintenanceBypass }));
      }
    } catch (err) {
      setError(err.message || 'Error al iniciar sesión');
    } finally {
      setLoading(false);
    }
  };

  const handleFirstPasswordChange = async (e) => {
    e.preventDefault();

    if (!canChangePassword) return;

    setChangeError('');

    if (newPassword !== confirmNewPassword) {
      setChangeError('Las contraseñas nuevas no coinciden.');
      return;
    }

    if (!passwordMeetsAllRules) {
      setChangeError(
        'La nueva contraseña debe tener mínimo 8 caracteres, incluir al menos una mayúscula, una minúscula, un número y un carácter especial.'
      );
      return;
    }

    if (newPassword === currentPassword) {
      setChangeError('La nueva contraseña debe ser distinta a la actual.');
      return;
    }

    setChangingPassword(true);

    try {
      const res = await fetch(`${API_BASE_URL}/api/auth/first-password-change`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          currentPassword,
          newPassword
        })
      });

      const data = await parseJsonResponse(res);

      if (!res.ok) {
        throw new Error(data.error || data.message || 'No se pudo actualizar la contraseña');
      }

      setShowFirstLoginModal(false);
      if (!getSyncAlreadyDone()) {
        await runInitialSync();
      }
      navigate(resolvePostLoginPath());
    } catch (err) {
      setChangeError(err.message || 'No se pudo actualizar la contraseña');
    } finally {
      setChangingPassword(false);
    }
  };

  const openForgotPanel = () => {
    setIsForgotFlipped(true);
    setForgotEmail('');
    setForgotError('');
    setForgotSuccess('');
    setResetError('');
  };

  const closeForgotPanel = () => {
    setIsForgotFlipped(false);
    setForgotError('');
    setForgotSuccess('');
    setResetError('');
    setResetPassword('');
    setResetConfirmPassword('');
    setResetToken('');
    navigate('/login', { replace: true });
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tokenFromUrl = String(params.get('resetToken') || '').trim();
    if (tokenFromUrl) {
      setResetToken(tokenFromUrl);
      setIsForgotFlipped(true);
      setForgotError('');
      setForgotSuccess('');
    }
  }, [location.search]);

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!canRequestForgot) return;

    setForgotError('');
    setForgotSuccess('');
    setForgotLoading(true);

    try {
      const res = await fetch(`${API_BASE_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ correo: forgotEmail.trim() })
      });
      const data = await parseJsonResponse(res);
      if (!res.ok) {
        throw new Error(data.error || data.message || 'No pudimos procesar la solicitud.');
      }
      const successMessage = data.message || 'Si el correo existe, enviaremos instrucciones de recuperación.';
      setForgotSuccess(successMessage);
      Swal.fire({
        toast: true,
        position: 'bottom-end',
        icon: 'success',
        html: `
          <div style="font-weight:700;color:#253342">Revisá tu correo</div>
          <div style="font-size:13px;color:#33475B;margin-top:6px">
            Te enviamos un enlace seguro para crear una nueva contraseña.
          </div>
        `,
        showConfirmButton: false,
        timer: 4500,
        timerProgressBar: true,
        customClass: { popup: 'swal-toast-crm' },
        background: '#fff'
      });
    } catch (err) {
      setForgotError(err.message || 'No pudimos procesar la solicitud.');
    } finally {
      setForgotLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!canSubmitReset) return;

    setResetError('');
    if (resetPassword !== resetConfirmPassword) {
      setResetError('Las contraseñas no coinciden.');
      return;
    }
    if (resetPassword.length < 8) {
      setResetError('La nueva contraseña debe tener al menos 8 caracteres.');
      return;
    }

    setResetLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          token: resetToken,
          newPassword: resetPassword,
          confirmPassword: resetConfirmPassword
        })
      });
      const data = await parseJsonResponse(res);
      if (!res.ok) {
        throw new Error(data.error || data.message || 'No se pudo actualizar la contraseña.');
      }

      setResetPassword('');
      setResetConfirmPassword('');
      setResetToken('');
      setIsForgotFlipped(false);
      navigate('/login', { replace: true });
      Swal.fire({
        toast: true,
        position: 'bottom-end',
        icon: 'success',
        html: `
          <div style="font-weight:700;color:#253342">Contraseña actualizada</div>
          <div style="font-size:13px;color:#33475B;margin-top:6px">
            Ya podés ingresar con tu nueva contraseña.
          </div>
        `,
        showConfirmButton: false,
        timer: 4500,
        timerProgressBar: true,
        customClass: { popup: 'swal-toast-crm' },
        background: '#fff'
      });
    } catch (err) {
      setResetError(err.message || 'No se pudo actualizar la contraseña.');
    } finally {
      setResetLoading(false);
    }
  };

  if (maintenanceActive && !isSuperAdminBypassMode) {
    return (
      <div style={styles.page} className="login-page login-page--premium login-maintenance-mode">
        <link
          href="https://fonts.googleapis.com/css2?family=Lexend:wght@400;500;600;700&display=swap"
          rel="stylesheet"
        />
        <style>
          {`
            .login-page,
            .login-page * {
              box-sizing: border-box;
            }
            .login-page {
              width: 100%;
              overflow-x: hidden;
            }
            @media (max-width: 900px) {
              .login-page { flex-direction: column !important; }
              .login-panel-left {
                width: 100% !important;
                flex: 0 0 100% !important;
                min-width: 0 !important;
                max-width: 100% !important;
                padding: 2rem 1.5rem !important;
                min-height: 220px !important;
                border-radius: 0 !important;
              }
              .login-panel-right {
                width: 100% !important;
                padding: 1.5rem !important;
                order: 2 !important;
                justify-content: flex-start !important;
              }
              .login-maintenance-mode .login-panel-left {
                order: 1 !important;
              }
              .login-card {
                width: 100% !important;
                max-width: 560px !important;
              }
            }
            @media (max-width: 640px) {
              .login-page {
                min-height: 100dvh;
                flex-direction: column !important;
              }
              .login-panel-left {
                display: none !important;
              }
              .login-panel-right {
                width: 100% !important;
                padding: 1rem !important;
                align-items: center !important;
                justify-content: center !important;
                order: 2 !important;
              }
              .login-maintenance-mode .login-panel-left {
                order: 1 !important;
              }
              .login-panel-content {
                max-width: none !important;
                width: 100% !important;
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                text-align: center;
              }
              .login-card {
                width: 100% !important;
                max-width: 100% !important;
                border-radius: 18px !important;
                padding: 1.25rem 0.95rem !important;
              }
              .login-maintenance-title {
                font-size: 1.2rem !important;
                line-height: 1.2 !important;
              }
              .login-maintenance-copy {
                font-size: 0.86rem !important;
                line-height: 1.45 !important;
                word-break: break-word;
              }
              .login-maintenance-help {
                font-size: 0.78rem !important;
              }
            }
            @media (max-width: 480px) {
              .login-panel-left {
                padding: 1.2rem 0.85rem !important;
              }
              .login-logo-circle {
                width: 64px !important;
                height: 64px !important;
                padding: 10px !important;
                border-radius: 16px !important;
              }
              .login-brand-title {
                font-size: 1.2rem !important;
              }
              .login-brand-sub {
                font-size: 0.84rem !important;
              }
              .login-brand-tag {
                font-size: 0.68rem !important;
                letter-spacing: 0.08em !important;
              }
              .login-card {
                padding: 1.05rem 0.8rem !important;
                border-radius: 16px !important;
              }
              .login-maintenance-title {
                font-size: 1.08rem !important;
              }
              .login-maintenance-copy {
                font-size: 0.8rem !important;
                line-height: 1.42 !important;
              }
              .login-maintenance-help {
                font-size: 0.74rem !important;
              }
            }
          `}
        </style>

        <div style={styles.panelLeft} className="login-panel-left">
          <div style={styles.panelContent} className="login-panel-content">
            <div style={styles.logoCircle} className="login-logo-circle">
              <img
                src="/pulseone.png"
                alt="Grupo Bienestar"
                style={styles.logoImg}
              />
            </div>
            <div className="login-brand-copy">
              <h1 style={styles.brandTitle} className="login-brand-title">Grupo Bienestar</h1>
              <p style={styles.brandSub} className="login-brand-sub">Gestión Asociativa</p>
              <p style={styles.brandTag} className="login-brand-tag">PLEX · GECLISA</p>
            </div>
          </div>
          <div style={styles.panelDecoration} className="login-panel-decoration" aria-hidden />
        </div>

        <div style={styles.panelRight} className="login-panel-right">
          <div style={styles.card} className="login-card login-maintenance-card">
            <div style={styles.cardTopGlow} aria-hidden />
            <div style={styles.cardHeader} className="login-card-header">
              <div style={styles.maintenanceBadge}>
                <span style={styles.maintenanceBadgeIcon}><UiIcon name="refresh" size={13} /></span>
                Mantenimiento activo
              </div>
              <h2 style={styles.cardTitle} className="login-maintenance-title">Grupo Bienestar en modo cuidado</h2>
              <p style={styles.cardSub} className="login-maintenance-copy">
                Estamos aplicando mejoras para que tus próximas sesiones sean más rápidas, seguras y fluidas.
              </p>
              <p style={styles.cardSub} className="login-maintenance-copy">
                El acceso al panel se encuentra pausado temporalmente. Intenta nuevamente en unos minutos.
              </p>
              <div style={styles.maintenanceTimeline}>
                <div style={styles.maintenanceTimelineItem}>
                  <span style={styles.maintenanceTimelineDot} aria-hidden />
                  <span>Optimización de rendimiento y estabilidad general.</span>
                </div>
                <div style={styles.maintenanceTimelineItem}>
                  <span style={styles.maintenanceTimelineDot} aria-hidden />
                  <span>Refuerzo de seguridad en sesiones y autenticación.</span>
                </div>
              </div>
              <div style={styles.maintenanceHelpBox} className="login-maintenance-copy login-maintenance-help">
                <span style={styles.maintenanceHelpIcon}><UiIcon name="shield" size={14} /></span>
                <span>
                  Necesitas operar ahora mismo? Contacta al equipo de sistemas de Grupo Santa Ana para coordinar
                  un acceso asistido.
                </span>
              </div>
            </div>

            {error && (
              <div style={styles.errorBox} role="alert">
                <span style={styles.errorIcon}>⚠️</span>
                <span>{error}</span>
              </div>
            )}

            <footer style={styles.footer} className="login-footer">
              <span style={styles.footerText}>
                © {new Date().getFullYear()} Grupo Bienestar · Gestión Asociativa
              </span>
            </footer>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page} className="login-page login-page--premium">
      <link
        href="https://fonts.googleapis.com/css2?family=Lexend:wght@400;500;600;700&display=swap"
        rel="stylesheet"
      />

      {/* Panel izquierdo: branding navy */}
      <div style={styles.panelLeft} className="login-panel-left">
        <div style={styles.panelContent} className="login-panel-content">
          <div style={styles.logoCircle} className="login-logo-circle">
            <img
              src="/pulseone.png"
              alt="Grupo Bienestar"
              style={styles.logoImg}
            />
          </div>
          <div className="login-brand-copy">
            <h1 style={styles.brandTitle} className="login-brand-title">Grupo Bienestar</h1>
            <p style={styles.brandSub} className="login-brand-sub">Gestión Asociativa</p>
            <p style={styles.brandTag} className="login-brand-tag">PLEX · GECLISA</p>
            <p style={styles.welcomeLine} className="login-welcome-line">{welcomeCopy}</p>
            <div style={styles.statusChip} className="login-status-chip">
              <span style={styles.statusDot} className="login-status-dot" aria-hidden />
              Plataforma operativa
            </div>
            <div style={styles.brandHighlights} className="login-brand-highlights">
              {LOGIN_HIGHLIGHTS.map((item) => (
                <div key={item} style={styles.brandHighlightItem} className="login-brand-highlight-item">
                  <span aria-hidden style={styles.brandHighlightDot} className="login-brand-highlight-dot" />
                  <span>{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div style={styles.panelDecoration} className="login-panel-decoration" aria-hidden />
      </div>

      {/* Panel derecho: card flip login/forgot */}
      <div style={styles.panelRight} className="login-panel-right">
        <div style={styles.flipContainer} className="login-flip-container">
          <div className={`login-flip-inner ${isForgotFlipped ? 'is-flipped' : ''}`}>
            <div style={styles.flipFace} className="login-flip-face login-flip-front">
              <div style={styles.card} className="login-card">
                <div className="login-card__glow" aria-hidden />
                <div style={styles.cardHeader} className="login-card-header">
                  <span className="login-card-eyebrow">Grupo Bienestar · CRM</span>
                  <h2 style={styles.cardTitle} className="login-card-title">
                    Iniciar sesión
                  </h2>
                  <p style={styles.cardSub} className="login-card-sub">
                    Ingresá con tu usuario de Grupo Bienestar
                  </p>
                  <div style={styles.securityPillsRow}>
                    <span style={styles.securityPill} className="login-security-pill">
                      {isSslActive ? 'SSL activo' : 'SSL inactivo'}
                    </span>
                    <span style={styles.securityPill} className="login-security-pill">
                      Sesión protegida
                    </span>
                  </div>
                </div>

                <form onSubmit={handleSubmit} style={styles.form}>
                  {error && (
                    <div style={styles.errorBox} className="login-error-box" role="alert">
                      <span style={styles.errorIcon}><UiIcon name="alert" size={15} /></span>
                      <span>{error}</span>
                    </div>
                  )}

                  <div style={styles.field}>
                    <label htmlFor="login-user" style={styles.label}>
                      Usuario
                    </label>
                    <div style={styles.inputWrapper} className="login-input-wrap">
                      <span style={styles.inputIcon}><UiIcon name="user" /></span>
                      <input
                        id="login-user"
                        type="text"
                        autoComplete="username"
                        autoFocus
                        placeholder="User"
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                        style={styles.input}
                      />
                    </div>
                    {!username && (
                      <span style={styles.hint}>Ingresá tu nombre de usuario</span>
                    )}
                  </div>

                  <div style={styles.field}>
                    <label htmlFor="login-pass" style={styles.label}>
                      Contraseña
                    </label>
                    <div style={styles.inputWrapper} className="login-input-wrap">
                      <span style={styles.inputIcon}><UiIcon name="lock" /></span>
                      <input
                        id="login-pass"
                        type={showPass ? 'text' : 'password'}
                        autoComplete="current-password"
                        placeholder="••••••••"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        style={styles.input}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPass(!showPass)}
                        style={styles.eyeButton}
                        aria-label={showPass ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                      >
                        <UiIcon name={showPass ? 'eye-off' : 'eye'} />
                      </button>
                    </div>
                    {!password && (
                      <span style={styles.hint}>Ingresá tu contraseña</span>
                    )}
                    {password ? (
                      <div style={styles.passwordStrengthRow}>
                        <span style={{ ...styles.passwordStrengthLabel, color: passwordStrength.tone }}>
                          {passwordStrength.label}
                        </span>
                        <div style={styles.passwordStrengthBars}>
                          {[1, 2, 3, 4].map((level) => (
                            <span
                              key={level}
                              style={{
                                ...styles.passwordStrengthBar,
                                background: level <= passwordStrength.score ? passwordStrength.tone : '#e2e8f0'
                              }}
                            />
                          ))}
                        </div>
                      </div>
                    ) : null}
                  </div>

                  <div style={styles.actionRow} className="login-action-row">
                    <button
                      type="submit"
                      className="login-submit-btn"
                      disabled={!canSubmit || loading}
                      style={{
                        ...styles.button,
                        ...styles.actionLeftButton,
                        marginTop: 0,
                        ...((!canSubmit || loading) && styles.buttonDisabled)
                      }}
                    >
                      {loading ? (
                        <span style={styles.buttonContent}>
                          <span style={styles.spinner} /> Ingresando…
                        </span>
                      ) : (
                        'Entrar'
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={openForgotPanel}
                      style={{
                        ...styles.secondaryButton,
                        ...styles.actionRightButton
                      }}
                    >
                      Olvidé mi contraseña
                    </button>
                  </div>
                </form>

                <footer style={styles.footer} className="login-footer">
                  <div style={styles.footerTrustList}>
                    {LOGIN_TRUST_ITEMS.map((item) => (
                      <div key={item.label} style={styles.trustItem}>
                        <span style={styles.trustIcon} className="login-trust-icon">
                          <UiIcon name={item.icon} size={13} stroke={2} />
                        </span>
                        <span>{item.label}</span>
                      </div>
                    ))}
                  </div>
                  <span style={styles.footerText}>
                    © {new Date().getFullYear()} Grupo Bienestar · Gestión Asociativa
                  </span>
                </footer>
              </div>
            </div>

            <div style={styles.flipFace} className="login-flip-face login-flip-back">
              <div style={styles.card} className="login-card">
                <div style={styles.cardHeader} className="login-card-header">
                  <h2 style={styles.cardTitle}>
                    {isResetMode ? 'Crear nueva contraseña' : 'Recuperar contraseña'}
                  </h2>
                  <p style={styles.cardSub}>
                    {isResetMode
                      ? 'Definí tu nueva contraseña para finalizar la recuperación.'
                      : 'Ingresá tu correo y te enviamos un enlace seguro para restablecerla.'}
                  </p>
                </div>

                <form onSubmit={isResetMode ? handleResetPassword : handleForgotPassword} style={styles.form}>
                  {forgotError && (
                    <div style={styles.errorBox} role="alert">
                      <span style={styles.errorIcon}><UiIcon name="alert" size={15} /></span>
                      <span>{forgotError}</span>
                    </div>
                  )}
                  {resetError && (
                    <div style={styles.errorBox} role="alert">
                      <span style={styles.errorIcon}><UiIcon name="alert" size={15} /></span>
                      <span>{resetError}</span>
                    </div>
                  )}
                  {!isResetMode ? (
                    <div style={styles.field}>
                      <label htmlFor="forgot-email-inline" style={styles.label}>
                        Correo
                      </label>
                      <div style={styles.inputWrapper} className="login-input-wrap">
                        <span style={styles.inputIcon}><UiIcon name="mail" /></span>
                        <input
                          id="forgot-email-inline"
                          type="email"
                          autoComplete="email"
                          placeholder="usuario@empresa.com"
                          value={forgotEmail}
                          onChange={(e) => setForgotEmail(e.target.value)}
                          style={styles.input}
                        />
                      </div>
                    </div>
                  ) : (
                    <>
                      <div style={styles.field}>
                        <label htmlFor="reset-pass-inline" style={styles.label}>
                          Nueva contraseña
                        </label>
                        <div style={styles.inputWrapper} className="login-input-wrap">
                          <span style={styles.inputIcon}><UiIcon name="lock" /></span>
                          <input
                            id="reset-pass-inline"
                            type={showResetPass ? 'text' : 'password'}
                            autoComplete="new-password"
                            placeholder="Mínimo 8 caracteres"
                            value={resetPassword}
                            onChange={(e) => setResetPassword(e.target.value)}
                            style={styles.input}
                          />
                          <button
                            type="button"
                            onClick={() => setShowResetPass(!showResetPass)}
                            style={styles.eyeButton}
                            aria-label={showResetPass ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                          >
                            <UiIcon name={showResetPass ? 'eye-off' : 'eye'} />
                          </button>
                        </div>
                      </div>
                      <div style={styles.field}>
                        <label htmlFor="reset-confirm-pass-inline" style={styles.label}>
                          Repetir contraseña
                        </label>
                        <div style={styles.inputWrapper} className="login-input-wrap">
                          <span style={styles.inputIcon}><UiIcon name="check" /></span>
                          <input
                            id="reset-confirm-pass-inline"
                            type={showResetConfirmPass ? 'text' : 'password'}
                            autoComplete="new-password"
                            placeholder="Repetí tu nueva contraseña"
                            value={resetConfirmPassword}
                            onChange={(e) => setResetConfirmPassword(e.target.value)}
                            style={styles.input}
                          />
                          <button
                            type="button"
                            onClick={() => setShowResetConfirmPass(!showResetConfirmPass)}
                            style={styles.eyeButton}
                            aria-label={showResetConfirmPass ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                          >
                            <UiIcon name={showResetConfirmPass ? 'eye-off' : 'eye'} />
                          </button>
                        </div>
                      </div>
                    </>
                  )}

                  <div style={styles.actionRow} className="login-action-row">
                    <button
                      type="submit"
                      className="login-submit-btn"
                      disabled={isResetMode ? !canSubmitReset : !canRequestForgot}
                      style={{
                        ...styles.button,
                        ...styles.actionLeftButton,
                        marginTop: 0,
                        ...((isResetMode ? !canSubmitReset : !canRequestForgot) && styles.buttonDisabled)
                      }}
                    >
                      {(isResetMode ? resetLoading : forgotLoading) ? (
                        <span style={styles.buttonContent}>
                          <span style={styles.spinner} /> {isResetMode ? 'Guardando…' : 'Enviando…'}
                        </span>
                      ) : (
                        isResetMode ? 'Guardar contraseña' : 'Enviar'
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={closeForgotPanel}
                      style={{
                        ...styles.secondaryButton,
                        ...styles.actionRightButton
                      }}
                    >
                      Volver al login
                    </button>
                  </div>
                </form>

                <footer style={styles.footer} className="login-footer">
                  <span style={styles.footerText}>
                    © {new Date().getFullYear()} Grupo Bienestar · Gestión Asociativa
                  </span>
                </footer>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showFirstLoginModal && (
        <div style={styles.modalOverlay} className="login-modal-overlay">
          <div style={styles.modal} className="login-modal-card">
            <div style={styles.modalHeader}>
              <div style={styles.modalIcon}><UiIcon name="spark" size={19} /></div>
              <h2 style={styles.modalTitle}>Actualizá tu contraseña</h2>
              <p style={styles.modalSub}>
                Por seguridad, necesitás definir una contraseña nueva la primera vez que ingresás.
              </p>
            </div>

            <form onSubmit={handleFirstPasswordChange} style={styles.modalForm}>
              {changeError && (
                <div style={styles.errorBox} role="alert">
                  <span style={styles.errorIcon}><UiIcon name="alert" size={15} /></span>
                  <span>{changeError}</span>
                </div>
              )}

              <div style={styles.field}>
                <label htmlFor="current-pass" style={styles.label}>
                  Contraseña actual
                </label>
                <div style={styles.inputWrapper} className="login-input-wrap">
                  <span style={styles.inputIcon}><UiIcon name="shield" /></span>
                  <input
                    id="current-pass"
                    type={showCurrentPass ? 'text' : 'password'}
                    autoComplete="current-password"
                    placeholder="Tu contraseña actual"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    style={styles.input}
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrentPass(!showCurrentPass)}
                    style={styles.eyeButton}
                    aria-label={showCurrentPass ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                  >
                    <UiIcon name={showCurrentPass ? 'eye-off' : 'eye'} />
                  </button>
                </div>
              </div>

              <div style={styles.field}>
                <label htmlFor="new-pass" style={styles.label}>
                  Nueva contraseña
                </label>
                <div style={styles.inputWrapper} className="login-input-wrap">
                  <span style={styles.inputIcon}><UiIcon name="lock" /></span>
                  <input
                    id="new-pass"
                    type={showNewPass ? 'text' : 'password'}
                    autoComplete="new-password"
                    placeholder="Mínimo 8 caracteres, mayúscula, minúscula, número y símbolo"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    style={styles.input}
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPass(!showNewPass)}
                    style={styles.eyeButton}
                    aria-label={showNewPass ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                  >
                    <UiIcon name={showNewPass ? 'eye-off' : 'eye'} />
                  </button>
                </div>
                {currentPassword &&
                  newPassword &&
                  !isNewPasswordDifferent && (
                    <span style={styles.errorTag}>
                      La nueva contraseña debe ser distinta a la actual.
                    </span>
                  )}
                {newPassword && (
                  <div style={styles.passwordChecklist}>
                    <div style={styles.passwordChecklistTitle}>
                      La contraseña debe tener:
                    </div>
                    <ul style={styles.passwordChecklistList}>
                      <li
                        style={{
                          ...styles.passwordChecklistItem,
                          ...(passwordMinLength && styles.passwordChecklistItemDone)
                        }}
                      >
                        <span style={styles.passwordChecklistIcon}>
                          {passwordMinLength ? '✔' : '•'}
                        </span>
                        <span>Mínimo 8 caracteres</span>
                      </li>
                      <li
                        style={{
                          ...styles.passwordChecklistItem,
                          ...(passwordHasUpper && styles.passwordChecklistItemDone)
                        }}
                      >
                        <span style={styles.passwordChecklistIcon}>
                          {passwordHasUpper ? '✔' : '•'}
                        </span>
                        <span>Al menos una letra mayúscula (A-Z)</span>
                      </li>
                      <li
                        style={{
                          ...styles.passwordChecklistItem,
                          ...(passwordHasLower && styles.passwordChecklistItemDone)
                        }}
                      >
                        <span style={styles.passwordChecklistIcon}>
                          {passwordHasLower ? '✔' : '•'}
                        </span>
                        <span>Al menos una letra minúscula (a-z)</span>
                      </li>
                      <li
                        style={{
                          ...styles.passwordChecklistItem,
                          ...(passwordHasNumber && styles.passwordChecklistItemDone)
                        }}
                      >
                        <span style={styles.passwordChecklistIcon}>
                          {passwordHasNumber ? '✔' : '•'}
                        </span>
                        <span>Al menos un número (0-9)</span>
                      </li>
                      <li
                        style={{
                          ...styles.passwordChecklistItem,
                          ...(passwordHasSpecial && styles.passwordChecklistItemDone)
                        }}
                      >
                        <span style={styles.passwordChecklistIcon}>
                          {passwordHasSpecial ? '✔' : '•'}
                        </span>
                        <span>Al menos un carácter especial (!@#$%^&amp;*...)</span>
                      </li>
                    </ul>
                  </div>
                )}
              </div>

              <div style={styles.field}>
                <label htmlFor="confirm-pass" style={styles.label}>
                  Confirmar nueva contraseña
                </label>
                <div style={styles.inputWrapper} className="login-input-wrap">
                  <span style={styles.inputIcon}><UiIcon name="check" /></span>
                  <input
                    id="confirm-pass"
                    type={showConfirmNewPass ? 'text' : 'password'}
                    autoComplete="new-password"
                    placeholder="Repetí la nueva contraseña"
                    value={confirmNewPassword}
                    onChange={(e) => setConfirmNewPassword(e.target.value)}
                    style={styles.input}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmNewPass(!showConfirmNewPass)}
                    style={styles.eyeButton}
                    aria-label={showConfirmNewPass ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                  >
                    <UiIcon name={showConfirmNewPass ? 'eye-off' : 'eye'} />
                  </button>
                </div>
                {confirmNewPassword && newPassword && !passwordsMatch && (
                  <span style={styles.errorTag}>
                    Las contraseñas nuevas no coinciden.
                  </span>
                )}
              </div>

              <div style={styles.modalActions}>
                <button
                  type="submit"
                  className="login-submit-btn"
                  disabled={!canChangePassword}
                  style={{
                    ...styles.button,
                    ...(!canChangePassword && styles.buttonDisabled)
                  }}
                >
                  {changingPassword ? (
                    <span style={styles.buttonContent}>
                      <span style={styles.spinner} /> Guardando nueva contraseña…
                    </span>
                  ) : (
                    'Guardar y continuar'
                  )}
                </button>
              </div>

              <p style={styles.modalHint}>
                Este paso es obligatorio solo la primera vez. Después vas a poder ingresar normalmente.
              </p>
            </form>
          </div>
        </div>
      )}

      <style>
        {`
          .login-page,
          .login-page * {
            box-sizing: border-box;
          }
          @keyframes login-spin {
            to { transform: rotate(360deg); }
          }
          @keyframes login-card-in {
            from { opacity: 0; transform: translateY(12px) scale(0.985); }
            to { opacity: 1; transform: translateY(0) scale(1); }
          }
          @keyframes login-logo-glow {
            0%,100% { box-shadow: 0 8px 24px rgba(0,0,0,0.2); }
            50% { box-shadow: 0 14px 34px rgba(35,101,171,0.32); }
          }
          .login-page {
            width: 100%;
            overflow-x: hidden;
          }
          .login-panel-left {
            background:
              radial-gradient(circle at top left, rgba(35,101,171,0.28), transparent 34%),
              linear-gradient(180deg, #253342 0%, #1f2b38 100%);
            isolation: isolate;
          }
          .login-panel-left::before {
            content: '';
            position: absolute;
            inset: 0;
            background:
              radial-gradient(circle at 20% 20%, rgba(255,255,255,0.07), transparent 22%),
              radial-gradient(circle at 80% 75%, rgba(35,101,171,0.12), transparent 28%);
            opacity: 0.9;
            pointer-events: none;
            z-index: 0;
          }
          .login-panel-left,
          .login-panel-right,
          .login-card,
          .login-modal-card,
          .login-sync-modal {
            max-width: 100%;
          }
          .login-panel-content,
          .login-card-header,
          .login-footer {
            width: 100%;
          }
          .login-flip-container {
            perspective: 1500px;
          }
          .login-flip-inner {
            position: relative;
            width: 100%;
            transform-style: preserve-3d;
            transition: transform 700ms cubic-bezier(0.22, 1, 0.36, 1);
          }
          .login-flip-inner.is-flipped {
            transform: rotateY(180deg);
          }
          .login-flip-face {
            backface-visibility: hidden;
            -webkit-backface-visibility: hidden;
            width: 100%;
          }
          .login-flip-back {
            position: absolute;
            inset: 0;
            transform: rotateY(180deg);
          }
          .login-panel-content {
            max-width: 320px;
            margin: 0 auto;
          }
          .login-panel-right {
            background:
              radial-gradient(circle at 8% 12%, rgba(35,101,171,0.08), transparent 25%),
              radial-gradient(circle at 88% 88%, rgba(37,99,235,0.09), transparent 30%),
              linear-gradient(180deg, #f8fbff 0%, #eef4fb 100%);
          }
          .login-card {
            position: relative;
            overflow: hidden;
            backdrop-filter: blur(10px);
            -webkit-backdrop-filter: blur(10px);
            animation: login-card-in .46s cubic-bezier(.22,1,.36,1);
            transition: transform .22s ease, box-shadow .22s ease;
          }
          .login-card:hover {
            transform: translateY(-2px);
            box-shadow: 0 28px 54px rgba(37,51,66,0.18), 0 0 0 1px rgba(0,0,0,0.04);
          }
          .login-card::before {
            content: '';
            position: absolute;
            width: 220px;
            height: 220px;
            border-radius: 50%;
            background: radial-gradient(circle, rgba(35,101,171,0.14), rgba(35,101,171,0));
            top: -120px;
            right: -100px;
            pointer-events: none;
          }
          .login-brand-copy {
            width: 100%;
          }
          .login-logo-circle {
            animation: login-logo-glow 3.2s ease-in-out infinite;
          }
          .login-panel-decoration {
            filter: blur(2px);
          }
          .login-input-wrap:focus-within {
            border-color: ${CRM.orange};
            box-shadow: 0 0 0 3px ${CRM.orangeLight};
          }
          .login-input-wrap input::placeholder {
            color: ${CRM.textXlight};
          }
          .login-submit-btn:not(:disabled):hover {
            background: ${CRM.orangeDark} !important;
            transform: translateY(-1px) scale(1.01);
          }
          .login-submit-btn:not(:disabled):active {
            transform: translateY(0);
          }
          .login-action-row button {
            min-height: 44px;
            transition: transform .18s ease, box-shadow .2s ease, background .18s ease;
          }
          .login-action-row button:hover:not(:disabled) {
            transform: translateY(-1px);
          }
          .login-sync-modal svg { animation: login-spin 1.3s linear infinite; }
          .login-modal-overlay {
            overflow-y: auto;
          }
          @media (max-width: 900px) {
            .login-page { flex-direction: column; }
            .login-maintenance-mode { flex-direction: column !important; }
            .login-panel-left {
              width: 100% !important;
              flex: 0 0 100% !important;
              min-width: 0 !important;
              max-width: 100% !important;
              padding: 2rem 1.5rem !important;
              min-height: 220px !important;
              border-radius: 0 !important;
            }
            .login-panel-right { padding: 1.5rem !important; }
            .login-maintenance-mode .login-panel-left {
              order: 1;
            }
            .login-maintenance-mode .login-panel-right {
              order: 2;
              width: 100% !important;
              justify-content: flex-start !important;
            }
            .login-card {
              max-width: 560px !important;
            }
            .login-flip-back {
              position: absolute;
              inset: 0;
            }
            .login-panel-content {
              max-width: none !important;
              width: 100% !important;
            }
          }
          @media (max-width: 640px) {
            .login-page {
              min-height: 100dvh;
            }
            .login-maintenance-mode {
              flex-direction: column !important;
            }
            .login-panel-left {
              display: none !important;
            }
            .login-panel-content {
              max-width: none !important;
              width: 100% !important;
              display: flex;
              flex-direction: column;
              align-items: center;
              justify-content: center;
              gap: 0;
              text-align: center;
            }
            .login-brand-copy {
              display: none !important;
            }
            .login-welcome-line {
              text-align: center !important;
            }
            .login-brand-highlights,
            .login-brand-copy > div {
              width: 100%;
            }
            .login-logo-circle {
              width: 78px !important;
              height: 78px !important;
              margin: 0 auto 1rem !important;
              border-radius: 18px !important;
              box-shadow: 0 10px 24px rgba(0, 0, 0, 0.22) !important;
            }
            .login-brand-title {
              font-size: 1.45rem !important;
              line-height: 1.05;
            }
            .login-brand-sub {
              font-size: 0.92rem !important;
              margin-top: 4px !important;
            }
            .login-brand-tag {
              font-size: 0.72rem !important;
              letter-spacing: 0.1em !important;
              margin-top: 6px !important;
            }
            .login-panel-decoration {
              width: 180px !important;
              height: 180px !important;
              right: -70px !important;
              bottom: -95px !important;
              opacity: 0.12 !important;
            }
            .login-panel-right {
              width: 100% !important;
              padding: 1rem !important;
              align-items: center !important;
              justify-content: center !important;
            }
            .login-maintenance-mode .login-panel-left {
              order: 1;
            }
            .login-maintenance-mode .login-panel-right {
              order: 2;
            }
            .login-card {
              width: 100% !important;
              max-width: 100% !important;
              border-radius: 18px !important;
              padding: 1.5rem 1rem !important;
            }
            .login-flip-back {
              position: absolute;
              inset: 0;
            }
            .login-maintenance-card {
              padding: 1.25rem 0.95rem !important;
            }
            .login-card-header {
              margin-bottom: 1.25rem !important;
            }
            .login-maintenance-title {
              font-size: 1.2rem !important;
              line-height: 1.2 !important;
            }
            .login-maintenance-copy {
              font-size: 0.86rem !important;
              line-height: 1.45 !important;
              word-break: break-word;
            }
            .login-maintenance-help {
              font-size: 0.78rem !important;
            }
            .login-input-wrap {
              padding: 11px 12px !important;
              gap: 8px !important;
            }
            .login-action-row {
              grid-template-columns: 1fr !important;
              gap: 8px !important;
            }
            .login-action-row .login-submit-btn {
              border-radius: 12px !important;
            }
            .login-action-row button {
              border-radius: 12px !important;
              margin-left: 0 !important;
              width: 100% !important;
            }
            .login-submit-btn {
              width: 100% !important;
            }
            .login-footer {
              margin-top: 1.5rem !important;
              padding-top: 1rem !important;
            }
            .login-modal-overlay {
              align-items: flex-start !important;
              padding: 0.85rem !important;
            }
            .login-modal-card,
            .login-sync-modal {
              width: 100% !important;
              max-width: 100% !important;
              border-radius: 18px !important;
              padding: 1.25rem 1rem 1.1rem !important;
              margin: auto 0 !important;
            }
          }
          @media (max-width: 480px) {
            .login-panel-content {
              width: 100% !important;
            }
            .login-logo-circle {
              width: 64px !important;
              height: 64px !important;
              padding: 10px !important;
              border-radius: 16px !important;
            }
            .login-brand-title {
              font-size: 1.2rem !important;
            }
            .login-brand-sub {
              font-size: 0.84rem !important;
            }
            .login-brand-tag {
              font-size: 0.68rem !important;
              letter-spacing: 0.08em !important;
            }
            .login-panel-decoration {
              width: 140px !important;
              height: 140px !important;
              right: -52px !important;
              bottom: -72px !important;
            }
            .login-card {
              padding: 1.2rem 0.9rem;
              border-radius: 16px;
            }
            .login-maintenance-card {
              padding: 1.05rem 0.8rem !important;
            }
            .login-maintenance-title {
              font-size: 1.08rem !important;
            }
            .login-maintenance-copy {
              font-size: 0.8rem !important;
              line-height: 1.42 !important;
            }
            .login-maintenance-help {
              font-size: 0.74rem !important;
            }
            .login-input-wrap {
              padding: 10px 11px;
            }
            .login-submit-btn {
              padding: 13px 16px !important;
              font-size: 0.92rem !important;
            }
            .login-modal-card,
            .login-sync-modal {
              padding: 1.1rem 0.9rem 1rem;
              border-radius: 16px;
            }
          }
        `}
      </style>

      {showSyncModal && (
        <div style={styles.modalOverlay} className="login-modal-overlay">
          <div style={styles.syncModal} className="login-sync-modal">
            <div style={styles.syncHeader}>
              <div style={styles.syncIcon}><UiIcon name="refresh" size={20} /></div>
              <h2 style={styles.syncTitle}>Sincronizando clientes</h2>
              <p style={styles.syncSub}>
                Estamos preparando la información para que puedas navegar sin esperas.
              </p>
            </div>

            <div style={styles.syncBarOuter}>
              <div
                style={{
                  ...styles.syncBarInner,
                  width: `${syncPercent}%`
                }}
              />
            </div>

            <div style={styles.syncMeta}>
              <span style={styles.syncPhase}>{syncPhase}</span>
              <span style={styles.syncPercent}>{syncPercent}%</span>
            </div>

            {!!syncDetail && (
              <p style={styles.syncDetail}>{syncDetail}</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/* ===================== STYLES ===================== */

const styles = {
  page: {
    minHeight: '100vh',
    width: '100%',
    display: 'flex',
    fontFamily: "'Lexend', system-ui, sans-serif",
    background: CRM.blueLight,
    overflowX: 'hidden'
  },

  /* Panel izquierdo */
  panelLeft: {
    flex: '0 0 42%',
    width: '42%',
    minWidth: 0,
    maxWidth: 480,
    background: CRM.navy,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '3rem 2rem',
    position: 'relative',
    overflow: 'hidden',
    boxSizing: 'border-box'
  },
  panelContent: {
    position: 'relative',
    zIndex: 1,
    textAlign: 'center',
    width: '100%'
  },
  logoCircle: {
    width: 100,
    height: 100,
    borderRadius: 20,
    background: CRM.orange,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 1.5rem',
    padding: 12,
    boxShadow: `0 8px 24px rgba(0,0,0,0.2)`
  },
  logoImg: {
    width: '100%',
    height: '100%',
    objectFit: 'contain'
  },
  brandTitle: {
    fontSize: '1.85rem',
    fontWeight: 700,
    color: CRM.white,
    margin: 0,
    letterSpacing: '-0.02em'
  },
  brandSub: {
    fontSize: '1rem',
    color: 'rgba(255,255,255,0.85)',
    marginTop: 6,
    fontWeight: 500
  },
  brandTag: {
    fontSize: '0.8rem',
    color: 'rgba(255,255,255,0.45)',
    marginTop: 8,
    letterSpacing: '0.12em',
    fontWeight: 500
  },
  welcomeLine: {
    marginTop: 10,
    fontSize: '0.8rem',
    color: 'rgba(255,255,255,0.82)',
    fontWeight: 500
  },
  statusChip: {
    marginTop: 12,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    fontSize: '0.72rem',
    fontWeight: 600,
    color: '#E2ECFF',
    background: 'rgba(148, 197, 255, 0.18)',
    border: '1px solid rgba(191, 219, 254, 0.45)',
    borderRadius: 999,
    padding: '4px 10px'
  },
  statusDot: {
    width: 7,
    height: 7,
    borderRadius: 999,
    background: '#4ADE80',
    boxShadow: '0 0 0 5px rgba(74, 222, 128, 0.18)'
  },
  brandHighlights: {
    marginTop: 18,
    display: 'grid',
    gap: 8,
    textAlign: 'left'
  },
  brandHighlightItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    color: 'rgba(255,255,255,0.78)',
    fontSize: '0.8rem'
  },
  brandHighlightDot: {
    width: 8,
    height: 8,
    borderRadius: 999,
    background: '#60A5FA',
    boxShadow: '0 0 0 4px rgba(96,165,250,0.2)',
    flexShrink: 0
  },
  panelDecoration: {
    position: 'absolute',
    bottom: -80,
    right: -80,
    width: 280,
    height: 280,
    borderRadius: '50%',
    background: CRM.orange,
    opacity: 0.08
  },

  /* Panel derecho */
  panelRight: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '2rem',
    minWidth: 0,
    boxSizing: 'border-box'
  },
  flipContainer: {
    width: '100%',
    maxWidth: 420
  },
  flipFace: {
    width: '100%'
  },
  card: {
    width: '100%',
    maxWidth: 400,
    padding: '2.5rem 2.25rem',
    borderRadius: 20,
    background: CRM.white,
    boxShadow: '0 24px 48px rgba(37, 51, 66, 0.14), 0 0 0 1px rgba(0,0,0,0.04)',
    boxSizing: 'border-box',
    overflow: 'hidden'
  },
  cardHeader: {
    marginBottom: '1.75rem'
  },
  // cardTopGlow: {
  //   position: 'absolute',
  //   top: -60,
  //   right: -60,
  //   width: 180,
  //   height: 180,
  //   borderRadius: 999,
  //   background: 'radial-gradient(circle, rgba(59,130,246,0.2), rgba(59,130,246,0))',
  //   pointerEvents: 'none'
  // },
  maintenanceBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    fontSize: '0.72rem',
    fontWeight: 700,
    color: '#1d4ed8',
    background: '#eff6ff',
    border: '1px solid rgba(37,99,235,.24)',
    borderRadius: 999,
    padding: '5px 10px',
    marginBottom: 10
  },
  maintenanceBadgeIcon: {
    width: 22,
    height: 22,
    borderRadius: 999,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#dbeafe',
    color: '#1d4ed8'
  },
  maintenanceTimeline: {
    marginTop: 12,
    display: 'grid',
    gap: 8,
    padding: '10px 12px',
    borderRadius: 12,
    background: '#f8fbff',
    border: `1px solid ${CRM.border}`
  },
  maintenanceTimelineItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    fontSize: '0.79rem',
    color: CRM.textLight,
    fontWeight: 500
  },
  maintenanceTimelineDot: {
    width: 8,
    height: 8,
    borderRadius: 999,
    background: '#3b82f6',
    boxShadow: '0 0 0 4px rgba(59,130,246,0.16)',
    flexShrink: 0
  },
  maintenanceHelpBox: {
    marginTop: 12,
    display: 'flex',
    alignItems: 'flex-start',
    gap: 8,
    padding: '10px 12px',
    borderRadius: 12,
    border: '1px dashed #cbd5e1',
    background: '#f8fafc',
    fontSize: '0.82rem',
    lineHeight: 1.45,
    color: CRM.textLight
  },
  maintenanceHelpIcon: {
    marginTop: 1,
    width: 22,
    height: 22,
    borderRadius: 999,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#e2e8f0',
    color: '#334155',
    flexShrink: 0
  },
  securityPillsRow: {
    marginTop: 10,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flexWrap: 'wrap'
  },
  securityPill: {
    fontSize: '0.7rem',
    fontWeight: 700,
    color: '#1d4ed8',
    background: '#eff6ff',
    border: '1px solid rgba(37,99,235,.24)',
    borderRadius: 999,
    padding: '4px 8px'
  },
  trustList: {
    marginTop: 12,
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: 6
  },
  footerTrustList: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: 6,
    marginBottom: 12,
    textAlign: 'left'
  },
  trustItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    color: CRM.textLight,
    fontSize: '0.76rem',
    fontWeight: 500
  },
  trustIcon: {
    width: 22,
    height: 22,
    borderRadius: 999,
    background: '#eff6ff',
    border: '1px solid rgba(37,99,235,.24)',
    color: '#1d4ed8',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0
  },
  cardTitle: {
    fontSize: '1.5rem',
    fontWeight: 700,
    color: CRM.navy,
    margin: 0,
    letterSpacing: '-0.02em'
  },
  cardSub: {
    fontSize: '0.9rem',
    color: CRM.textLight,
    marginTop: 6
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: 20
  },
  actionRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 0,
    width: '100%'
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6
  },
  label: {
    fontSize: '0.8rem',
    fontWeight: 600,
    color: CRM.navyMid,
    letterSpacing: '0.02em'
  },
  inputWrapper: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    width: '100%',
    border: `1px solid ${CRM.borderDark}`,
    borderRadius: 10,
    padding: '12px 14px',
    background: CRM.white,
    transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
    boxSizing: 'border-box'
  },
  inputIcon: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#4b6584',
    opacity: 0.9,
    flexShrink: 0
  },
  input: {
    flex: 1,
    minWidth: 0,
    border: 'none',
    outline: 'none',
    fontSize: '0.95rem',
    color: CRM.text,
    background: 'transparent'
  },
  eyeButton: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: '#516F90',
    padding: 4,
    opacity: 0.8,
    flexShrink: 0
  },
  hint: {
    fontSize: '0.75rem',
    color: CRM.textXlight
  },
  passwordStrengthRow: {
    marginTop: 6,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8
  },
  passwordStrengthLabel: {
    fontSize: '0.72rem',
    fontWeight: 700
  },
  passwordStrengthBars: {
    display: 'inline-flex',
    gap: 4
  },
  passwordStrengthBar: {
    width: 24,
    height: 5,
    borderRadius: 999,
    transition: 'background .18s ease'
  },
  button: {
    marginTop: 8,
    width: '100%',
    padding: '11px 10px',
    borderRadius: 10,
    background: CRM.orange,
    color: CRM.white,
    border: 'none',
    fontSize: '0.82rem',
    fontWeight: 600,
    fontFamily: "'Lexend', system-ui, sans-serif",
    cursor: 'pointer',
    transition: 'background 0.2s ease, transform 0.1s ease'
  },
  secondaryButton: {
    marginTop: 0,
    width: '100%',
    padding: '11px 10px',
    borderRadius: 10,
    background: '#F8FAFC',
    color: CRM.navyMid,
    border: `1px solid ${CRM.borderDark}`,
    fontSize: '0.82rem',
    fontWeight: 600,
    fontFamily: "'Lexend', system-ui, sans-serif",
    cursor: 'pointer'
  },
  buttonDisabled: {
    opacity: 0.6,
    cursor: 'not-allowed',
    transform: 'none'
  },
  actionLeftButton: {
    borderRadius: '10px 0 0 10px'
  },
  actionRightButton: {
    borderRadius: '0 10px 10px 0',
    marginLeft: '-1px'
  },
  buttonContent: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    width: '100%',
    flexWrap: 'wrap'
  },
  spinner: {
    width: 16,
    height: 16,
    border: `2px solid rgba(255,255,255,0.3)`,
    borderTopColor: CRM.white,
    borderRadius: '50%',
    animation: 'login-spin 0.7s linear infinite'
  },
  errorBox: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 10,
    background: '#FEF2F2',
    color: '#B91C1C',
    border: '1px solid #FECACA',
    padding: '12px 14px',
    borderRadius: 10,
    fontSize: '0.875rem',
    fontWeight: 500
  },
  errorIcon: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  successBox: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 10,
    background: '#ECFDF3',
    color: '#166534',
    border: '1px solid #A7F3D0',
    padding: '12px 14px',
    borderRadius: 10,
    fontSize: '0.875rem',
    fontWeight: 500
  },
  successIcon: {
    fontSize: '1rem'
  },
  errorTag: {
    marginTop: 4,
    alignSelf: 'flex-start',
    fontSize: '0.78rem',
    fontWeight: 500,
    color: '#B91C1C',
    background: '#FEE2E2',
    borderRadius: 999,
    padding: '2px 8px'
  },
  passwordChecklist: {
    marginTop: 8,
    padding: '8px 10px 6px',
    borderRadius: 10,
    background: CRM.blueLight,
    border: `1px dashed ${CRM.borderDark}`
  },
  passwordChecklistTitle: {
    fontSize: '0.78rem',
    fontWeight: 600,
    color: CRM.textLight,
    marginBottom: 4
  },
  passwordChecklistList: {
    listStyle: 'none',
    padding: 0,
    margin: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: 2
  },
  passwordChecklistItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    fontSize: '0.78rem',
    color: CRM.textXlight
  },
  passwordChecklistItemDone: {
    color: CRM.textLight,
    textDecoration: 'line-through',
    opacity: 0.8
  },
  passwordChecklistIcon: {
    width: 16,
    height: 16,
    borderRadius: 999,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '0.7rem',
    background: CRM.white,
    border: `1px solid ${CRM.borderDark}`
  },
  footer: {
    marginTop: '2rem',
    paddingTop: '1.5rem',
    borderTop: `1px solid ${CRM.border}`,
    textAlign: 'center',
    width: '100%'
  },
  footerText: {
    fontSize: '0.75rem',
    color: CRM.textXlight
  },

  /* Modal primer login */
  modalOverlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(15, 23, 42, 0.55)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '1.5rem',
    zIndex: 30,
    backdropFilter: 'blur(6px)',
    overflowY: 'auto',
    boxSizing: 'border-box'
  },
  modal: {
    width: '100%',
    maxWidth: 460,
    borderRadius: 20,
    background: CRM.white,
    boxShadow: '0 20px 60px rgba(15, 23, 42, 0.35)',
    padding: '2rem 2rem 1.75rem',
    position: 'relative',
    border: `1px solid ${CRM.border}`,
    boxSizing: 'border-box'
  },
  modalHeader: {
    marginBottom: '1.5rem',
    textAlign: 'left'
  },
  modalIcon: {
    width: 40,
    height: 40,
    borderRadius: 999,
    background: CRM.orangeLight,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 10,
    color: CRM.orangeDark
  },
  modalTitle: {
    margin: 0,
    fontSize: '1.3rem',
    fontWeight: 700,
    color: CRM.navy
  },
  modalSub: {
    marginTop: 6,
    fontSize: '0.9rem',
    color: CRM.textLight
  },
  modalForm: {
    display: 'flex',
    flexDirection: 'column',
    gap: 14
  },
  modalActions: {
    marginTop: 10
  },
  modalActionsTwo: {
    marginTop: 10,
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 10
  },
  modalHint: {
    marginTop: 10,
    fontSize: '0.78rem',
    color: CRM.textXlight
  },
  /* Modal de sincronización inicial */
  syncModal: {
    width: '100%',
    maxWidth: 440,
    borderRadius: 20,
    background: CRM.white,
    boxShadow: '0 20px 60px rgba(15, 23, 42, 0.35)',
    padding: '1.8rem 1.8rem 1.6rem',
    position: 'relative',
    border: `1px solid ${CRM.border}`,
    boxSizing: 'border-box'
  },
  syncHeader: {
    marginBottom: '1.1rem'
  },
  syncIcon: {
    width: 38,
    height: 38,
    borderRadius: 999,
    background: CRM.orangeLight,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 8,
    color: CRM.orangeDark
  },
  syncTitle: {
    margin: 0,
    fontSize: '1.2rem',
    fontWeight: 700,
    color: CRM.navy
  },
  syncSub: {
    marginTop: 4,
    fontSize: '0.88rem',
    color: CRM.textLight
  },
  syncBarOuter: {
    width: '100%',
    height: 9,
    borderRadius: 999,
    background: CRM.blueLight,
    overflow: 'hidden',
    position: 'relative',
    marginBottom: 8,
    border: `1px solid ${CRM.border}`
  },
  syncBarInner: {
    height: '100%',
    borderRadius: 999,
    background: 'linear-gradient(90deg, #2365AB, #FFB347)',
    transition: 'width 0.25s ease-out'
  },
  syncMeta: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 10,
    flexWrap: 'wrap',
    fontSize: '0.8rem',
    color: CRM.textLight,
    marginTop: 4
  },
  syncPhase: {
    fontWeight: 500
  },
  syncPercent: {
    fontFamily: "'Lexend', system-ui, sans-serif",
    fontWeight: 600,
    color: CRM.orangeDark
  },
  syncDetail: {
    marginTop: 6,
    fontSize: '0.78rem',
    color: CRM.textXlight
  },
  forgotRow: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: -6
  },
  linkButton: {
    background: 'transparent',
    border: 'none',
    padding: 0,
    color: CRM.orange,
    fontWeight: 600,
    cursor: 'pointer',
    fontSize: '0.82rem'
  }
};

export default Login;
